import {PDFDocumentProxy} from 'pdfjs-dist';

import {isDestroyedPdfDocument} from './PdfViewer.utils';

export type PdfHighlight = {
  pageIndex: number;
  content: string;
};

/**
 * A highlight scan can outlive the document it scans — the viewer switches to
 * another file, or react-pdf hands out a document it has already abandoned — and
 * the page reads then fail with a TypeError.
 */
function isScanPointless(
  document: PDFDocumentProxy,
  signal: AbortSignal
): boolean {
  return signal.aborted || isDestroyedPdfDocument(document);
}

async function getPageHighlights(
  document: PDFDocumentProxy,
  pageNumber: number,
  signal: AbortSignal
): Promise<PdfHighlight[]> {
  if (isScanPointless(document, signal)) {
    return [];
  }

  try {
    const page = await document.getPage(pageNumber);
    const annotations = await page.getAnnotations();
    return annotations
      .filter((annotation) => annotation.subtype === 'Highlight')
      .map((annotation) => ({
        pageIndex: page.pageNumber - 1,
        content: annotation.contentsObj.str,
      }));
  } catch (error) {
    if (!isScanPointless(document, signal)) {
      // report the page we could not read, but keep the highlights of the others
      // eslint-disable-next-line no-console
      console.error(`Failed to read highlights of page ${pageNumber}`, error);
    }
    return [];
  }
}

/**
 * Reads the highlights of all pages, or resolves with `null` if the document
 * was abandoned before the scan could finish.
 */
export async function getPdfHighlights(
  document: PDFDocumentProxy,
  signal: AbortSignal
): Promise<PdfHighlight[] | null> {
  if (isScanPointless(document, signal)) {
    return null;
  }

  const pageNumbers = Array.from(
    {length: document.numPages},
    (_, index) => index + 1
  );

  const annotationsWithPage = await Promise.all(
    pageNumbers.map((pageNumber) =>
      getPageHighlights(document, pageNumber, signal)
    )
  );

  if (isScanPointless(document, signal)) {
    return null;
  }

  return annotationsWithPage
    .flatMap((highlights) => highlights)
    .filter((highlight, index, highlights) => {
      const indexOfHighlight = highlights.findIndex(
        (h) =>
          h.pageIndex === highlight.pageIndex && h.content === highlight.content
      );
      return indexOfHighlight === index;
    });
}
